import type { ActivityStatus } from '@obraplan/core/domain/structure/activity';
import { formatQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import {
  describeSummary,
  type StructureTree,
} from '@obraplan/core/domain/structure/structure-tree';
import { formatShortDate } from '@/lib/format';

export type TreeNodeType = 'TOWER' | 'FLOOR' | 'ACTIVITY' | 'ITEM';

export interface FlatTreeNode {
  readonly id: string;
  readonly type: TreeNodeType;
  readonly parentId: string | undefined;
  /** 1 = torre, 2 = pavimento, 3 = atividade, 4 = item — usado em `aria-level`. */
  readonly level: number;
  readonly label: string;
  readonly summary: string;
  readonly hasChildren: boolean;
  readonly color: string | undefined;
  readonly status: ActivityStatus | undefined;
  /** O nível do pavimento (`0`, `12`, `-1`), como no corte do prédio. Só o pavimento tem. */
  readonly marker: string | undefined;
  /** Posição entre irmãos, para `aria-posinset` / `aria-setsize`. */
  readonly positionInSet: number;
  readonly setSize: number;
}

/**
 * Achata a árvore nos nós **visíveis**, na ordem em que aparecem na tela.
 *
 * Duas coisas dependem disso: a navegação por teclado (que precisa saber quem vem antes
 * e depois) e o custo de renderização — um nó recolhido não gera DOM para os filhos, o
 * que mantém a árvore leve mesmo em obra grande sem precisar de virtualização.
 */
export function flattenTree(tree: StructureTree, expanded: ReadonlySet<string>): FlatTreeNode[] {
  const nodes: FlatTreeNode[] = [];

  tree.towers.forEach((towerNode, towerIndex) => {
    const towerId = towerNode.tower.id;
    nodes.push({
      id: towerId,
      type: 'TOWER',
      parentId: undefined,
      level: 1,
      label: towerNode.tower.name,
      summary: describeSummary(towerNode.summary, 'tower'),
      hasChildren: towerNode.floors.length > 0,
      color: undefined,
      status: undefined,
      marker: undefined,
      positionInSet: towerIndex + 1,
      setSize: tree.towers.length,
    });

    if (!expanded.has(towerId)) return;

    towerNode.floors.forEach((floorNode, floorIndex) => {
      const floorId = floorNode.floor.id;
      nodes.push({
        id: floorId,
        type: 'FLOOR',
        parentId: towerId,
        level: 2,
        label: floorNode.floor.name,
        summary: describeSummary(floorNode.summary, 'floor'),
        hasChildren: floorNode.activities.length > 0,
        color: undefined,
        status: undefined,
        marker: String(floorNode.floor.level),
        positionInSet: floorIndex + 1,
        setSize: towerNode.floors.length,
      });

      if (!expanded.has(floorId)) return;

      floorNode.activities.forEach((activityNode, activityIndex) => {
        const activityId = activityNode.activity.id;
        nodes.push({
          id: activityId,
          type: 'ACTIVITY',
          parentId: floorId,
          level: 3,
          label: activityNode.discipline.name,
          summary:
            activityNode.itemCount === 0
              ? ''
              : `${String(activityNode.itemCount)} ${activityNode.itemCount === 1 ? 'item' : 'itens'}`,
          hasChildren: activityNode.items.length > 0,
          color: activityNode.discipline.color,
          status: activityNode.activity.status,
          marker: undefined,
          positionInSet: activityIndex + 1,
          setSize: floorNode.activities.length,
        });

        if (!expanded.has(activityId)) return;

        // Último nível: material, quantidade e a data em que ele precisa estar na obra.
        activityNode.items.forEach((itemNode, itemIndex) => {
          nodes.push({
            id: itemNode.entry.id,
            type: 'ITEM',
            parentId: activityId,
            level: 4,
            label: `${itemNode.material.name} · ${formatQuantity(itemNode.entry.quantity, itemNode.unit)}`,
            summary: formatShortDate(itemNode.entry.requiredDate),
            hasChildren: false,
            color: undefined,
            status: undefined,
            marker: undefined,
            positionInSet: itemIndex + 1,
            setSize: activityNode.items.length,
          });
        });
      });
    });
  });

  return nodes;
}

/** Todos os ids expansíveis — usado por "expandir tudo". */
export function allExpandableIds(tree: StructureTree): string[] {
  const ids: string[] = [];
  for (const towerNode of tree.towers) {
    ids.push(towerNode.tower.id);
    for (const floorNode of towerNode.floors) {
      if (floorNode.activities.length > 0) ids.push(floorNode.floor.id);
      for (const activityNode of floorNode.activities) {
        if (activityNode.items.length > 0) ids.push(activityNode.activity.id);
      }
    }
  }
  return ids;
}

/** O que a tela precisa saber para renomear um nó — nada além disso. */
export interface RenameTarget {
  readonly type: 'TOWER' | 'FLOOR';
  readonly id: string;
  readonly name: string;
  readonly code: string | undefined;
  /** Nível real na edificação. Só existe para pavimento (RN-005). */
  readonly level: number | undefined;
}

/**
 * Localiza torre ou pavimento na árvore já carregada.
 *
 * A tela de renomear não precisa de uma consulta nova: o nó está na árvore que ela já
 * tem em mãos, e a busca aqui é sobre um punhado de itens.
 */
export function findRenameTarget(tree: StructureTree, nodeId: string): RenameTarget | undefined {
  for (const towerNode of tree.towers) {
    if (towerNode.tower.id === nodeId) {
      return {
        type: 'TOWER',
        id: nodeId,
        name: towerNode.tower.name,
        code: towerNode.tower.code,
        level: undefined,
      };
    }
    for (const floorNode of towerNode.floors) {
      if (floorNode.floor.id === nodeId) {
        return {
          type: 'FLOOR',
          id: nodeId,
          name: floorNode.floor.name,
          code: floorNode.floor.code,
          level: floorNode.floor.level,
        };
      }
    }
  }
  return undefined;
}

/** Item já resolvido, do jeito que o formulário de edição precisa. */
export interface ItemTarget {
  readonly entryId: string;
  readonly activityId: string;
  readonly activityLabel: string;
  readonly materialId: string;
  readonly materialName: string;
  readonly quantity: number;
  readonly unitId: string;
  readonly requiredDate: string;
  readonly plannedStart: string | undefined;
  readonly plannedEnd: string | undefined;
  readonly notes: string | undefined;
}

/** Localiza o item e o rótulo da frente a que pertence, para o título do diálogo. */
export function findItemTarget(tree: StructureTree, entryId: string): ItemTarget | undefined {
  for (const towerNode of tree.towers) {
    for (const floorNode of towerNode.floors) {
      for (const activityNode of floorNode.activities) {
        for (const itemNode of activityNode.items) {
          if (itemNode.entry.id !== entryId) continue;
          return {
            entryId,
            activityId: activityNode.activity.id,
            activityLabel: `${activityNode.discipline.name} · ${floorNode.floor.name}`,
            materialId: itemNode.material.id,
            materialName: itemNode.material.name,
            quantity: itemNode.entry.quantity,
            unitId: itemNode.entry.unitId,
            requiredDate: itemNode.entry.requiredDate,
            plannedStart: itemNode.entry.plannedStart,
            plannedEnd: itemNode.entry.plannedEnd,
            notes: itemNode.entry.notes,
          };
        }
      }
    }
  }
  return undefined;
}

/** Rótulo da atividade — `"Alvenaria · 3º Pavimento"`, para o diálogo de novo item. */
export function findActivityLabel(tree: StructureTree, activityId: string): string | undefined {
  for (const towerNode of tree.towers) {
    for (const floorNode of towerNode.floors) {
      for (const activityNode of floorNode.activities) {
        if (activityNode.activity.id === activityId) {
          return `${activityNode.discipline.name} · ${floorNode.floor.name}`;
        }
      }
    }
  }
  return undefined;
}
